import { useContext, useEffect, useState } from 'react'
import type { ApolloError } from '@apollo/client'
import { Maybe } from 'graphql/jsutils/Maybe'
import { FilterContext } from '../../../context/FilterContext'
import {
  CategoryTrendPercentage,
  EmissionMetaData,
  useEmissionMetaDataLazyQuery,
} from '../../../graphql/generated'
import { useFilterInput } from '../../../hooks/useFilterInput'
import type { IFilterDatePeriod } from '../../../utils/datePeriod'

type Metadata = {
  metaData:
    | (Pick<
        EmissionMetaData,
        'totalEmission' | 'yearlyPercentage' | 'emissionPerAnnualWorkForce'
      > & {
        worstCategory?: Maybe<
          Pick<CategoryTrendPercentage, 'percentage' | 'diff' | 'categoryId'>
        >
        bestCategory?: Maybe<
          Pick<CategoryTrendPercentage, 'percentage' | 'diff' | 'categoryId'>
        >
      })
    | undefined
  loading: boolean
  error: ApolloError | undefined
  datePeriod: IFilterDatePeriod
  isAllYears: boolean
}

export const useMetaData = (): Metadata => {
  const { defaultDatePeriod } = useContext(FilterContext)
  const filter = useFilterInput()
  const [isAllYears, setIsAllYears] = useState(false)

  const [getMetadata, { data, loading, error }] = useEmissionMetaDataLazyQuery()

  useEffect(() => {
    setIsAllYears(false)
    getMetadata({ variables: { filter } }).then((result) => {
      // if there is no enough data to get metadata for specific years, fall bak to all years
      if (
        !result.error &&
        (result.data?.getMetaData.totalEmission === undefined ||
          result.data?.getMetaData.totalEmission === null)
      ) {
        setIsAllYears(true)
        getMetadata({ variables: { filter: { ...filter, year: undefined } } })
      }
    })
  }, [filter, getMetadata])

  if (data?.getMetaData) {
    return {
      metaData: data.getMetaData,
      loading,
      error: error,
      datePeriod: defaultDatePeriod,
      isAllYears,
    }
  } else {
    return {
      metaData: undefined,
      loading,
      error: error,
      datePeriod: defaultDatePeriod,
      isAllYears,
    }
  }
}
